/**
 * PATH: packages/email-hub/src/feature/components/EmailJobContextRail.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-EMAILJOBCONTEXTRAIL-001
 * PURPOSE: Canonical body of this email components module. Lifted
 *          from apps/<tenant>/src/features/email/components/EmailJobContextRail.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/EmailJobContextRail.tsx: thin re-export
 *     shim that points back here.
 */

import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, Mail, Paperclip, Sparkles, User } from 'lucide-react'

import { cn } from '../../_internal/lib/utils'

import AttachmentList from './AttachmentList'
import AttachmentPreviewModal from './AttachmentPreviewModal'
import IntelligenceWorkspace from './IntelligenceWorkspace'
import { resolveSyncedMailbox } from './email-context/emailContextFormat'
import { JobSiblingEmailList } from './email-context/JobSiblingEmailList'
import { SyncedFromDetails } from './email-context/SyncedFromDetails'
import { useJobSiblingEmails } from './email-context/useJobSiblingEmails'
import { selectVisibleAttachments } from '../lib/utils'
import type { Email } from '../hooks/useEmails'

interface EmailJobContextRailProps {
  email: Email
  /** Called when the user clicks a sibling email in the list — the
   *  parent reader swaps to that email without a route change. */
  onJumpTo: (siblingId: string) => void
  /** Forwarded to AIEmailPanel for the rare cases it triggers an
   *  outbound action (link-to-job / create-job / reply). */
  onAIAction?: (action: string, payload: Record<string, unknown>) => void
  /** Render below the email body as a full-width block instead of as a
   *  fixed 320px right-hand column. The reader sets this on narrow
   *  viewports, where the side-by-side form left the body ~55px wide
   *  (`w-80 shrink-0` never yielded) inside an `overflow-hidden` row, so
   *  the rail both crushed the message and could not be scrolled to.
   *
   *  Stacked mode also gives up its own scrollport: the reader's body
   *  column is the single scroller, so a touch drag anywhere in the
   *  context sections keeps moving the same surface instead of hitting a
   *  nested scroll trap. */
  stacked?: boolean
  /** The reader header is showing the Synced-from / Other-emails chips, so
   *  those two sections must NOT also be rendered here. Set together with
   *  `stacked` by ReaderContent — one flag decides both, because a chip
   *  without the section suppressed duplicates the information and a section
   *  without a chip is the buried-below-the-message state this replaced. */
  relatedInHeader?: boolean
}

export default function EmailJobContextRail({
  email,
  onJumpTo,
  onAIAction,
  stacked = false,
  relatedInHeader = false,
}: EmailJobContextRailProps) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  // Operator 2026-06-13 ("by default have all sections expanded — you
  // don't want to click when scrolling down"). AI Analysis starts OPEN
  // and auto-populates (see autoAnalyze below); the header toggle still
  // collapses it for anyone who wants the rail compact.
  //
  // Stacked mode inverts the DEFAULT only, never the capability. Below the
  // message on a phone this section is last in a single column, so
  // expanding it by default buries the sibling list the operator came for
  // AND fires the classify request on every open — a cost paid even when
  // the analysis is never read. Collapsed keeps it one tap away and leaves
  // the wide rail exactly as asked for above.
  const [aiOpen, setAiOpen] = useState(!stacked)

  // Canonical operator-visible attachment list (inline cid: images
  // dropped, smallest-first). Same reference feeds AttachmentList and
  // AttachmentPreviewModal so the chip's `onPreview(idx)` index aligns
  // with the carousel — see selectVisibleAttachments.
  const visibleAttachments = useMemo(
    () => selectVisibleAttachments(email.attachments),
    [email.attachments],
  )

  // Siblings: every linked email on the same job, from the shared
  // ['job-emails', jobId] cache entry JobDetailPage also seeds, so the rail
  // populates instantly when the user opened the reader from the Job → Emails
  // tab. See email-context/useJobSiblingEmails.ts.
  const { jobId, siblings, isPending: siblingsPending } = useJobSiblingEmails(email)
  const mailbox = resolveSyncedMailbox(email)

  return (
    <>
      {/* Stacked: `-mx-6 mt-6` cancels the reader body column's `p-6` so the
          block spans the full column width and its top border reads as a real
          divider under the message. No `overflow-y-auto` — the body column is
          the single scroller in this mode.

          `pb-24` is bottom-chrome clearance, not decoration. The reader is
          INLINE in the job page (JobEmailsTab), which floats a files pill at
          `fixed bottom-20 right-4 z-30` below `md`. Without trailing space the
          last control here — the AI Analysis toggle — comes to rest under that
          pill and cannot be scrolled clear of it. */}
      <div
        className={cn(
          'bg-muted/10 flex flex-col',
          stacked
            ? '-mx-6 mt-6 border-t border-border pb-24'
            : 'w-80 border-l border-border overflow-y-auto shrink-0',
        )}
        data-testid="email-job-context-rail"
        data-rail-layout={stacked ? 'stacked' : 'column'}
      >
        {/* Section 1 — Mailbox-of-origin badge. Suppressed while the header
            carries the Synced From chip (same block, one press away). */}
        {mailbox && !relatedInHeader && (
          <div className="px-4 py-3 border-b border-border/60">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5">
              <User className="h-3 w-3" /> Synced from
            </p>
            <SyncedFromDetails mailbox={mailbox} />
          </div>
        )}

        {/* Section 2 — Attachments (promoted from body to first-class).
            We deliberately render the same <AttachmentList> component
            the body uses so the file affordance is identical - chips
            in a 2-col grid, click to preview, right-click for "save
            to disk" / "allocate to bill". The preview modal is mounted
            once, here, and addressed via index. */}
        {email.has_attachments && visibleAttachments.length > 0 && (
          <div className="px-4 py-3 border-b border-border/60">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <Paperclip className="h-3 w-3" /> Attachments ({visibleAttachments.length})
            </p>
            <AttachmentList
              attachments={visibleAttachments}
              emailId={email.id}
              onPreview={(idx) => setPreviewIndex(idx)}
            />
          </div>
        )}
        {email.has_attachments && (email.attachments?.length ?? 0) === 0 && (
          <div className="px-4 py-3 border-b border-border/60 text-[11px] text-muted-foreground">
            <Paperclip className="h-3 w-3 inline mr-1" />
            Attachments still hydrating - open the email body to retry.
          </div>
        )}

        {/* Section 3 — Other emails on this job. The whole point of
            replacing the AI rail: an operator reading remittance
            advice wants to flip to the underlying invoice email or
            the bill confirmation in two clicks, not two route
            changes. Hidden when the email isn't linked to a job
            (the reader is being used in inbox-mode, not job-mode), and
            when the header carries the Other Emails chip. */}
        {jobId != null && !relatedInHeader && (
          <div
            className={cn(
              'px-4 py-3 border-b border-border/60',
              // Column mode: the sibling list absorbs the rail's slack and
              // scrolls within it. Stacked mode must NOT — a scrollport here
              // would sit inside the body column's scrollport and swallow
              // touch drags aimed at the page.
              !stacked && 'flex-1 overflow-y-auto min-h-0',
            )}
          >
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> Other emails on this job
              <span className="text-foreground/60">({siblings.length})</span>
            </p>
            <JobSiblingEmailList
              siblings={siblings}
              isPending={siblingsPending}
              onJumpTo={onJumpTo}
            />
          </div>
        )}

        {/* Section 4 — AI Analysis (EXPANDED by default; operator
            2026-06-13 "have all sections expanded, you don't want to
            click when scrolling down"). Still a collapsible accordion
            so anyone who wants sibling navigation + attachments to
            dominate can click it shut - but it auto-populates so the
            analysis is visible without a click. */}
        <div className="border-t border-border/60">
          <button
            type="button"
            onClick={() => setAiOpen(o => !o)}
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> AI Analysis
            </span>
            {aiOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>
          {aiOpen && (
            <div className="px-2 pb-3">
              <IntelligenceWorkspace
                email={email}
                compact
                autoAnalyze
                onActionClick={onAIAction}
              />
            </div>
          )}
        </div>
      </div>

      <AttachmentPreviewModal
        attachments={visibleAttachments}
        initialIndex={previewIndex ?? 0}
        isOpen={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        emailId={email?.id ?? null}
      />
    </>
  )
}
